/**
 * SmsOutboxCard · v2.7.65+E70.1
 * ══════════════════════════════════════════════════════════════════════
 * Live SMS Outbox viewer for the System Health page. Polls
 * `/api/admin/sms/outbox?limit=50` every 30 s (pausable) and renders:
 *   • Counters — total delivered, total failed, Twilio-configured chip
 *   • Filter row — All / Failed only / by kind
 *   • Table  — timestamp · to · kind · status · body preview · [Replay]
 *
 * "Replay" hits `POST /api/admin/sms/outbox/{id}/replay` and refreshes.
 * Only failed rows show the button; delivered rows are read-only.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { MessageSquare, RefreshCw, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const POLL_MS = 30_000;

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    }
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function StatusPill({ delivered, error }) {
  if (delivered) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
        <CheckCircle2 className="h-3 w-3"/> Delivered
      </span>
    );
  }
  const isConfig = String(error || "").toLowerCase().includes("not configured");
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${isConfig ? "bg-amber-500/15 text-amber-300 border-amber-500/40" : "bg-red-500/15 text-red-300 border-red-500/40"}`}>
      {isConfig ? <AlertTriangle className="h-3 w-3"/> : <XCircle className="h-3 w-3"/>}
      {isConfig ? "Not Configured" : "Failed"}
    </span>
  );
}

export default function SmsOutboxCard() {
  const [rows, setRows] = useState([]);
  const [counters, setCounters] = useState({ delivered: 0, failed: 0 });
  const [twilioConfigured, setTwilioConfigured] = useState(false);
  const [kinds, setKinds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");   // all | delivered | failed
  const [kindFilter, setKindFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [autoPoll, setAutoPoll] = useState(true);
  const [replayingId, setReplayingId] = useState(null);
  const lastLoadRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "50" });
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (kindFilter !== "all")   params.set("kind", kindFilter);
      const { data } = await adminApi.get(`/admin/sms/outbox?${params.toString()}`);
      setRows(data.items || []);
      setCounters(data.counters || { delivered: 0, failed: 0 });
      setTwilioConfigured(!!data.twilio_configured);
      setKinds(data.kinds || []);
      lastLoadRef.current = new Date();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SMS Outbox load failed");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, kindFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoPoll) return undefined;
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [autoPoll, load]);

  const replay = useCallback(async (row) => {
    if (row?.delivered) return;
    setReplayingId(row.id);
    try {
      const { data } = await adminApi.post(`/admin/sms/outbox/${row.id}/replay`);
      if (data.ok) toast.success(`Replayed to ${row.to}`);
      else toast.error(`Replay failed: ${data.result?.error || "unknown"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Replay failed");
    } finally {
      setReplayingId(null);
    }
  }, [load]);

  const summaryLine = useMemo(() => {
    const parts = [];
    parts.push(`${counters.delivered} delivered`);
    parts.push(`${counters.failed} failed`);
    if (lastLoadRef.current) parts.push(`last checked ${lastLoadRef.current.toLocaleTimeString()}`);
    return parts.join(" · ");
  }, [counters]);

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid="sms-outbox-card">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-amber-400"/>
          <div>
            <h3 className="font-bold text-sm">SMS Outbox</h3>
            <div className="text-[10px] text-zinc-500 mt-0.5" data-testid="sms-outbox-summary">{summaryLine}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {twilioConfigured ? (
            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 text-[10px] uppercase tracking-wider" data-testid="twilio-configured-chip">
              Twilio configured
            </Badge>
          ) : (
            <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40 text-[10px] uppercase tracking-wider" data-testid="twilio-not-configured-chip">
              Twilio not configured
            </Badge>
          )}
          <label className="flex items-center gap-2 cursor-pointer" data-testid="sms-outbox-autopoll-toggle">
            <Switch checked={autoPoll} onCheckedChange={setAutoPoll}/>
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-300">Auto · 30 s</span>
          </label>
          <Button
            onClick={load}
            disabled={loading}
            variant="outline"
            className="h-8 border-zinc-700 hover:bg-zinc-800 text-xs"
            data-testid="sms-outbox-refresh"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin"/> : <RefreshCw className="h-3 w-3"/>}
            <span className="ml-1">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Status</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-7 w-32 bg-zinc-950 border-zinc-700 text-xs" data-testid="sms-outbox-filter-status">
              <SelectValue/>
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="failed">Failed only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {kinds.length > 0 && (
          <div className="flex items-center gap-1">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Kind</span>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="h-7 w-48 bg-zinc-950 border-zinc-700 text-xs" data-testid="sms-outbox-filter-kind">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 max-h-64">
                <SelectItem value="all">All kinds</SelectItem>
                {kinds.map((k) => (
                  <SelectItem key={k} value={k}>{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded border border-zinc-800 overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-zinc-900/60 text-[9px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left px-2 py-1.5">Time</th>
              <th className="text-left px-2 py-1.5">To</th>
              <th className="text-left px-2 py-1.5">Kind</th>
              <th className="text-center px-2 py-1.5">Status</th>
              <th className="text-left px-2 py-1.5">Preview</th>
              <th className="text-right px-2 py-1.5">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800/70">
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-2 py-6 text-center text-zinc-500" data-testid="sms-outbox-empty">
                  No SMS attempts recorded yet.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-zinc-950/40" data-testid={`sms-outbox-row-${r.id}`}>
                <td className="px-2 py-1.5 text-zinc-400 font-mono">{fmtTs(r.created_at)}</td>
                <td className="px-2 py-1.5 text-zinc-200 font-mono">{r.to || "—"}</td>
                <td className="px-2 py-1.5 text-zinc-300 uppercase tracking-wide text-[10px]">{r.kind || "generic"}</td>
                <td className="px-2 py-1.5 text-center">
                  <StatusPill delivered={r.delivered} error={r.error}/>
                </td>
                <td className="px-2 py-1.5 text-zinc-400 truncate max-w-md" title={r.body || ""}>
                  {(r.body || "").slice(0, 80)}{(r.body || "").length > 80 ? "…" : ""}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {r.delivered ? (
                    <span className="text-[10px] text-zinc-600 font-mono">
                      {r.twilio_sid ? r.twilio_sid.slice(-6) : "—"}
                    </span>
                  ) : r.replayed_at ? (
                    <span
                      className="text-[10px] text-zinc-500 font-mono"
                      title={`Replayed at ${r.replayed_at} by ${r.replayed_by || "?"} · ${r.replay_delivered ? "OK" : "still failing"}`}
                    >
                      replayed
                    </span>
                  ) : (
                    <Button
                      onClick={() => replay(r)}
                      disabled={replayingId === r.id}
                      className="h-6 px-2 bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-bold"
                      data-testid={`sms-outbox-replay-${r.id}`}
                    >
                      {replayingId === r.id ? <Loader2 className="h-2.5 w-2.5 animate-spin"/> : <RefreshCw className="h-2.5 w-2.5"/>}
                      <span className="ml-1">Replay</span>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!twilioConfigured && rows.length > 0 && (
        <p className="text-[10px] text-amber-300/80 mt-2" data-testid="sms-outbox-config-hint">
          Twilio credentials aren&apos;t saved yet — messages are being logged here but NOT delivered. Set them in
          <span className="mx-1 text-amber-200 font-semibold">Admin → Payments → Twilio · System-wide SMS</span>
          and then use Replay on the failed rows above.
        </p>
      )}
    </Card>
  );
}
